import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const EmployeeEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/employees",
    token
  );

  // Fetch shops for selection (foreign key)
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [salary, setSalary] = useState("");
  const [age, setAge] = useState("");
  const [contractDate, setContractDate] = useState("");
  const [contractDurations, setContractDurations] = useState("");

  // Set initial values from editData.
  useEffect(() => {
    if (editData) {
      setSelectedShop(
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : ""
      );
      setName(editData.name || "");
      setEmail(editData.email || "");
      setContact(editData.contact || "");
      setAddress(editData.address || "");
      setSalary(editData.salary || "");
      setAge(editData.age || "");
      setContractDate(editData.contract_date || "");
      setContractDurations(editData.contract_durations || "");
    }
  }, [editData]);

  // Validate shop selection once shops load.
  useEffect(() => {
    if (editData && shops && shops.length > 0) {
      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      const exists = (shops || []).find((s) => String(s.id) === shopId);
      if (exists) {
        setSelectedShop(shopId);
      } else {
        setSelectedShop("");
      }
    }
  }, [editData, shops]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("contact", contact);
    formData.append("address", address);
    formData.append("salary", salary);
    formData.append("age", age);
    formData.append("contract_date", contractDate);
    formData.append("contract_durations", contractDurations);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedShop("");
      setName("");
      setEmail("");
      setContact("");
      setAddress("");
      setSalary("");
      setAge("");
      setContractDate("");
      setContractDurations("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("employee.editEmployee")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("employee.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("employee.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.email")}
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.contact")}
            fullWidth
            required
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.address")}
            fullWidth
            required
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.salary")}
            type="number"
            fullWidth
            required
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.age")}
            type="number"
            fullWidth
            required
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("employee.contractDate")}
            type="date"
            fullWidth
            required
            value={contractDate}
            onChange={(e) => setContractDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t("employee.contractDurations")}
            fullWidth
            required
            value={contractDurations}
            onChange={(e) => setContractDurations(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("employee.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !name ||
            !email ||
            !contact ||
            !address ||
            !salary ||
            !age ||
            !contractDate ||
            !contractDurations
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("employee.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeEdit;
